import { events } from './enums';
import { logAmplitudeEvent } from 'choco-app';
import { CLIENT_ID, NATIVE_APP_AGENT } from '@/utils/constants';
import { application } from '@/utils/plugins/di/providers';
import { TABLET_SESSION, useSession } from '@/utils/composables/use-session';

// Ленивая загрузка Amplitude: тяжёлый SDK уезжает из entry в отдельный чанк.
// В webview события идут через logAmplitudeEvent (choco-app), поэтому там SDK
// не грузится вовсе; в вебе — подгружается и инициализируется отдельным чанком.
type AmplitudeModule = typeof import('@amplitude/analytics-browser');

export interface AnalyticsPayload {
	project_id: string;
	project_name: string;
	project_type: 'web' | 'miniapp' | 'partnerapp';
	service_name: string;
	[key: string]: any;
}

interface AuthProvider {
	getUserId: () => string | null;
}

interface ClientProvider {
	getServiceId: () => string;
}

export class Analytics {
	private isLoggable: boolean;
	private isDev: boolean;
	private isWebView: boolean;
	private key: string;
	private AuthProvider: AuthProvider;
	private ClientProvider: ClientProvider;
	private amplitudeReady: Promise<AmplitudeModule> | null = null;

	constructor(AuthProvider: AuthProvider, ClientProvider: ClientProvider) {
		this.key = import.meta.env.VITE_AMPLITUDE_KEY;
		this.AuthProvider = AuthProvider;
		this.ClientProvider = ClientProvider;
		this.isWebView = application.isWebView();
		this.isLoggable = import.meta.env.VITE_ENV !== 'production';
		this.isDev = import.meta.env.VITE_ENV === 'local';

		// В webview/dev Amplitude SDK не нужен — не грузим. В вебе кикаем
		// ленивую загрузку заранее (fire-and-forget), чтобы к первому событию
		// чанк уже был готов; из entry SDK при этом уходит.
		if (!this.isWebView && !this.isDev) {
			this.loadAmplitude();
		}
	}

	private loadAmplitude(): Promise<AmplitudeModule> {
		if (!this.amplitudeReady) {
			this.amplitudeReady = import('@amplitude/analytics-browser').then(
				async amplitude => {
					// init в amplitude v2 асинхронный — дожидаемся его промиса до
					// того, как кто-то вызовет track, иначе событие отклоняется с
					// "Event rejected due to missing API key".
					await amplitude.init(this.key, { autocapture: false }).promise;
					return amplitude;
				}
			);
		}

		return this.amplitudeReady;
	}

	private getProjectType(): 'web' | 'miniapp' | 'partnerapp' {
		if (this.isWebView) return 'miniapp';
		if (navigator.userAgent.includes(NATIVE_APP_AGENT)) return 'partnerapp';
		return 'web';
	}

	async logEvent(
		event: events | string,
		customPayload: Record<string, any> = {},
		options: { flush?: boolean } = {}
	): Promise<void> {
		const projectType = this.getProjectType();
		const basePayload: AnalyticsPayload = {
			project_id: CLIENT_ID,
			project_name: 'digital restaurant',
			project_type: projectType,
			service_name: this.ClientProvider.getServiceId(),
			...customPayload
		};

		const { getSession } = useSession(TABLET_SESSION);

		if (this.isLoggable) {
			console.log(event, basePayload);
		}

		if (this.isDev) return;

		if (this.isWebView) {
			logAmplitudeEvent(event, basePayload);
			return;
		}

		const amplitude = await this.loadAmplitude();
		const userId = this.AuthProvider.getUserId();
		const customSessionId = getSession();
		if (customSessionId) {
			amplitude.setUserId(customSessionId);

			if (userId) {
				basePayload.user_id = userId;
			}
		} else {
			amplitude.setUserId(userId || undefined);
		}

		// Для событий перед навигацией (reload/redirect) переключаемся на
		// sendBeacon и форсим flush, иначе батч Amplitude не успевает уйти
		// по сети до ухода со страницы и событие теряется.
		if (options.flush) {
			amplitude.setTransport('beacon');
			amplitude.track(event, basePayload);
			await amplitude.flush().promise;
			return;
		}

		amplitude.track(event, basePayload);
	}
}

export { events };
